import { Controller } from "@hotwired/stimulus";
import { HorizontalRailCarousel } from "../HorizontalRail/HorizontalRailCarousel";
import { formatTranslation, webTranslations } from "../webTranslations";

type ResponsiveImageHeaderModel = { imageCount: number };

export default class ResponsiveImageHeaderController extends Controller<HTMLElement> {
    static values = { model: Object };

    declare readonly modelValue: ResponsiveImageHeaderModel;

    private carousel?: HorizontalRailCarousel;
    private resizeFrame?: number;

    connect(): void {
        const saved = Number.parseInt(this.element.dataset.responsiveImageHeaderActiveIndex ?? "0", 10);
        if (this.rail) {
            this.carousel = new HorizontalRailCarousel({
                root: this.element, rail: this.rail,
                slideSelector: "[data-responsive-image-header-slide]",
                onSelect: index => this.select(index),
                snapToNearest: () => window.matchMedia("(min-width: 768px)").matches
            });
            this.carousel.connect(Number.isFinite(saved) ? saved : 0);
        }
        this.rail?.addEventListener("scroll", this.onScroll, { passive: true });
        this.rail?.addEventListener("keydown", this.onKeyDown);
        this.element.addEventListener("error", this.onImageError, true);
        this.element.addEventListener("load", this.onImageLoad, true);
        window.addEventListener("resize", this.onResize);
        document.addEventListener("turbo:before-cache", this.onBeforeCache);

        this.element.querySelectorAll<HTMLImageElement>("img").forEach(image => {
            if (image.getAttribute("src") && image.complete && image.naturalWidth === 0) image.hidden = true;
        });
    }

    disconnect(): void {
        this.onBeforeCache();
        this.rail?.removeEventListener("scroll", this.onScroll);
        this.rail?.removeEventListener("keydown", this.onKeyDown);
        this.element.removeEventListener("error", this.onImageError, true);
        this.element.removeEventListener("load", this.onImageLoad, true);
        window.removeEventListener("resize", this.onResize);
        document.removeEventListener("turbo:before-cache", this.onBeforeCache);
        if (this.resizeFrame !== undefined) cancelAnimationFrame(this.resizeFrame);
        this.carousel?.disconnect();
        this.carousel = undefined;
    }

    next(event: Event): void {
        event.preventDefault();
        this.carousel?.step(1);
    }

    previous(event: Event): void {
        event.preventDefault();
        this.carousel?.step(-1);
    }

    private select(index: number): void {
        const slides = this.slides;
        if (slides.length === 0) return;
        const count = slides.length;
        const selected = Math.max(0, Math.min(index, count - 1));
        this.element.dataset.responsiveImageHeaderActiveIndex = String(selected);
        for (const slide of this.element.querySelectorAll<HTMLElement>("[data-responsive-image-header-slide]")) {
            const slideIndex = Number(slide.dataset.responsiveImageHeaderIndex);
            const image = slide.querySelector<HTMLImageElement>("img");
            const active = slideIndex === selected && !slide.hasAttribute("data-horizontal-rail-copy");
            slide.setAttribute("aria-hidden", String(!active));
            if (!image) continue;
            const distance = Math.min((slideIndex - selected + count) % count, (selected - slideIndex + count) % count);
            if (distance <= 2 && image.dataset.responsiveImageHeaderSrc) {
                image.loading = "eager";
                const source = slide.querySelector<HTMLSourceElement>("source[data-responsive-image-header-srcset]");
                if (source?.dataset.responsiveImageHeaderSrcset) {
                    source.srcset = source.dataset.responsiveImageHeaderSrcset;
                    delete source.dataset.responsiveImageHeaderSrcset;
                }
                image.src = image.dataset.responsiveImageHeaderSrc;
                delete image.dataset.responsiveImageHeaderSrc;
            }
            image.alt = active ? image.dataset.responsiveImageHeaderAlt ?? "" : "";
        }

        this.updatePosition(selected, count);
        this.onScroll();
    }

    private updatePosition(index: number, count: number): void {
        const pagination = this.element.querySelector<HTMLElement>("[data-responsive-image-header-pagination]");
        if (!pagination) return;
        pagination.setAttribute("aria-label", formatTranslation(webTranslations.photoPositionFormat, index + 1, count));

        // Match the native five-indicator window, with 6px and 4px continuation dots.
        const mainCount = Math.min(count, 5);
        const savedStart = Number.parseInt(pagination.dataset.responsiveImageHeaderPaginationStart ?? "0", 10);
        let start = Math.max(0, Math.min(Number.isFinite(savedStart) ? savedStart : 0, count - mainCount));
        if (index < start) start = index;
        if (index >= start + mainCount) start = index - mainCount + 1;
        pagination.dataset.responsiveImageHeaderPaginationStart = String(start);
        for (const [dotIndex, dot] of Array.from(pagination.querySelectorAll<HTMLElement>("[data-responsive-image-header-pagination-dot]")).entries()) {
            const distance = dotIndex < start ? start - dotIndex : dotIndex - (start + mainCount - 1);
            dot.dataset.responsiveImageHeaderIndicator = distance <= 0 ? "main" : distance === 1 ? "near" : "far";
            dot.classList.toggle("responsive-image-header__pagination-dot--active", dotIndex === index);
        }
        const track = pagination.querySelector<HTMLElement>("[data-responsive-image-header-pagination-track]");
        if (track) track.style.transform = `translateX(-${start === 0 ? 0 : start * 10 + 2}px)`;
    }

    private readonly onScroll = (): void => {
        const rail = this.rail;
        if (!rail || rail.clientWidth === 0) return;
        const center = rail.scrollLeft + rail.clientWidth / 2;
        for (const slide of rail.querySelectorAll<HTMLElement>("[data-responsive-image-header-slide]")) {
            const distance = Math.abs(center - slide.offsetLeft - slide.offsetWidth / 2) / (slide.offsetWidth || 1);
            slide.style.setProperty("--hero-photo-shade", String(Math.min(1, distance) * .52));
        }
    };

    private readonly onKeyDown = (event: KeyboardEvent): void => {
        if (event.key === "ArrowRight") this.next(event);
        if (event.key === "ArrowLeft") this.previous(event);
    };

    private readonly onImageError = (event: Event): void => {
        if (event.target instanceof HTMLImageElement) event.target.hidden = true;
    };

    private readonly onImageLoad = (event: Event): void => {
        if (event.target instanceof HTMLImageElement) event.target.hidden = false;
    };

    private readonly onResize = (): void => {
        if (this.resizeFrame !== undefined) cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => {
            this.resizeFrame = undefined;
            this.carousel?.reflow(this.activeIndex);
        });
    };

    private readonly onBeforeCache = (): void => {
        this.element.dataset.responsiveImageHeaderActiveIndex = String(this.activeIndex);
    };

    private get rail(): HTMLElement | null {
        return this.element.querySelector<HTMLElement>("[data-responsive-image-header-rail]");
    }

    private get slides(): HTMLElement[] {
        return Array.from(this.element.querySelectorAll<HTMLElement>("[data-responsive-image-header-slide]:not([data-horizontal-rail-copy])"));
    }

    private get activeIndex(): number {
        return Number.parseInt(this.element.dataset.responsiveImageHeaderActiveIndex ?? "0", 10) || 0;
    }
}
